import {
   CalendarDays,
   ChevronRight,
   Dumbbell,
   Flame,
   House,
   LineChart,
   Medal,
   Shield,
   Star,
   Trophy,
   UserRound,
   Volleyball,
} from "lucide-react";
import "./TeamBasketballPublic.css";
import { useEffect, useState } from "react";
import api from "@api";
import { useDomain } from "@useDomain";
import { useParams } from "react-router-dom";
const leaders = [
   {
      label: "Leading Scorer",
      sub: "Points Per Game",
      name: "Steven Williams",
      value: "9.4",
      stat: "PPG",
      initials: "SW",
      icon: Star,
   },
   {
      label: "Rebound Leader",
      sub: "Rebounds Per Game",
      name: "Kaheem Stewart",
      value: "6.8",
      stat: "RPG",
      initials: "KS",
      icon: Volleyball,
   },
   {
      label: "3PT Leader",
      sub: "3-Pointers Made",
      name: "Randall Bowman",
      value: "12",
      stat: "3PM",
      initials: "RB",
      icon: Flame,
   },
   {
      label: "Assist Leader",
      sub: "Assists Per Game",
      name: "Derrick Shaw",
      value: "3.2",
      stat: "APG",
      initials: "DS",
      icon: Dumbbell,
   },
];

const achievements = [
   {
      icon: Trophy,
      title: "Championship",
      season: "2023 Spring",
      text: "Hitters took home the championship with a dominant playoff run.",
   },
   {
      icon: Medal,
      title: "Runner-Up",
      season: "2022 Fall",
      text: "Outstanding season and a hard-fought championship game.",
   },
   {
      icon: Star,
      title: "Best Regular Season Record",
      season: "2021 Spring",
      text: "Finished the regular season with a 9-1 record.",
   },
];

const seasonHistory = [
   { season: "2026 Spring", record: "4 - 5", win: ".444", finish: "—" },
   { season: "2025 Fall", record: "6 - 4", win: ".600", finish: "Playoffs" },
   { season: "2025 Spring", record: "7 - 3", win: ".700", finish: "Playoffs" },
   { season: "2024 Fall", record: "5 - 5", win: ".500", finish: "—" },
   { season: "2024 Spring", record: "8 - 2", win: ".800", finish: "Playoffs" },
];

function PlayersTeamBasketball() {
   const [teamData, setTeamData] = useState(null);
   const { slug, domain } = useDomain();
   const { sltId } = useParams();
   useEffect(() => {
      const loadTeam = async () => {
         try {
            const res = await api.get(`/api/seasonsLeagues/public/slt/${sltId}`, {
               params: slug ? { slug } : { domain },
            });

            console.log("SLT TEAM TEST:", res.data);
            setTeamData(res.data);
         } catch (err) {
            console.error("Error loading SLT team:", err);
         }
      };

      loadTeam();
   }, [slug, domain]);

   if (!teamData) return null;

   const team = teamData.team;
   const roster = teamData.roster || [];
   const games = team?.games || [];

   const formatDate = (date) => {
      if (!date) return "Date TBD";

      return new Date(date + "T00:00:00Z").toLocaleDateString("en-US", {
         month: "short",
         day: "numeric",
         year: "numeric",
         timeZone: "UTC",
      });
   };

   const getInitials = (player) => {
      const first = player?.firstName?.charAt(0) || "";
      const last = player?.lastName?.charAt(0) || "";
      return `${first}${last}`.toUpperCase() || "P";
   };

   const isCompletedGame = (game) => {
      const home = Number(game?.score_home);
      const away = Number(game?.score_away);

      if (Number.isNaN(home) || Number.isNaN(away)) return false;
      if (home === 0 && away === 0) return false;

      return true;
   };

   const getOpponentName = (game) => {
      const isHome = Number(game.home_team_id) === Number(team.sltId);

      return isHome
         ? game.away_team?.team?.name || "Opponent TBD"
         : game.home_team?.team?.name || "Opponent TBD";
   };

   const getScore = (game) => {
      if (game.score_home === null || game.score_away === null) return "-";
      return `${game.score_home} - ${game.score_away}`;
   };

   const getResult = (game) => {
      if (!isCompletedGame(game)) return "Scheduled";

      const isHome = Number(game.home_team_id) === Number(team.sltId);
      const myScore = isHome ? Number(game.score_home) : Number(game.score_away);
      const oppScore = isHome ? Number(game.score_away) : Number(game.score_home);

      if (myScore > oppScore) return "Win";
      if (myScore < oppScore) return "Loss";
      return "Tie";
   };

   const completedGames = games.filter(isCompletedGame);

   const results = completedGames
      .slice()
      .sort((a, b) => new Date(b.date) - new Date(a.date))
      .slice(0, 5);

   const nextGame =
      games
         .filter((game) => !isCompletedGame(game))
         .sort((a, b) => new Date(a.date) - new Date(b.date))[0] || null;

   const wins = completedGames.filter((game) => getResult(game) === "Win").length;
   const losses = completedGames.filter((game) => getResult(game) === "Loss").length;

   return (
      <div className="sh-bball-team-page">
         <main className="sh-bball-team-shell">
            <section className="sh-bball-team-hero sh-bball-team-hero--no-badge">
               <div className="sh-bball-team-hero-main">
                  <p>{team?.season?.name || "Basketball"}</p>
                  <h1>{team?.name || "Team Name"}</h1>

                  <div className="sh-bball-team-captain-row">
                     <Shield size={17} />
                     <span>Captain: {team?.captain || "—"}</span>
                  </div>

                  <div className="sh-bball-team-stat-row">
                     <div className="sh-bball-team-stat-card">
                        <Trophy size={23} />
                        <div>
                           <p>Record</p>
                           <strong>{wins} - {losses}</strong>
                        </div>
                     </div>

                     <div className="sh-bball-team-stat-card">
                        <CalendarDays size={23} />
                        <div>
                           <p>Games</p>
                           <strong>{games.length}</strong>
                        </div>
                     </div>

                     <div className="sh-bball-team-stat-card">
                        <LineChart size={23} />
                        <div>
                           <p>League</p>
                           <strong>{team?.league?.name || "—"}</strong>
                        </div>
                     </div>

                     <div className="sh-bball-team-stat-card">
                        <Flame size={23} />
                        <div>
                           <p>Players</p>
                           <strong>{roster.length}</strong>
                        </div>
                     </div>
                  </div>
               </div>

               <aside className="sh-bball-team-next-card">
                  <p>Next Game</p>

                  {nextGame ? (
                     <>
                        <h3>vs {getOpponentName(nextGame)}</h3>
                        <span>
                           {formatDate(nextGame.date)}
                           {nextGame.time ? ` · ${nextGame.time}` : ""}
                        </span>
                        <small>{nextGame.location || team?.league?.name || "Location TBD"}</small>

                        <div className="sh-bball-team-mini-matchup">
                           <div>
                              <div className="sh-bball-team-mini-logo sh-bball-team-mini-logo--orange">
                                 {team?.name?.charAt(0) || "T"}
                              </div>
                              <strong>{team?.name?.slice(0, 3)?.toUpperCase()}</strong>
                           </div>

                           <span>vs</span>

                           <div>
                              <div className="sh-bball-team-mini-logo sh-bball-team-mini-logo--navy">
                                 {getOpponentName(nextGame)?.charAt(0) || "O"}
                              </div>
                              <strong>{getOpponentName(nextGame)?.slice(0, 3)?.toUpperCase()}</strong>
                           </div>
                        </div>

                        <button>View Game Details</button>
                     </>
                  ) : (
                     <>
                        <h3>No upcoming game</h3>
                        <span>Schedule has not been added.</span>
                     </>
                  )}
               </aside>
            </section>

            <section className="sh-bball-team-photo-banner">
               <div className="sh-bball-team-photo-overlay">
                  {roster.slice(0, 9).map((item) => (
                     <div className="sh-bball-team-player-shape" key={item.id}>
                        {item.jersey_number || getInitials(item.player)}
                     </div>
                  ))}
               </div>
            </section>

            <section className="sh-bball-team-grid">
               <div className="sh-bball-team-card sh-bball-team-roster-card">
                  <div className="sh-bball-team-card-header">
                     <h3>Roster Preview</h3>
                     <a>
                        View Full Roster <ChevronRight size={15} />
                     </a>
                  </div>

                  <table className="sh-bball-team-table">
                     <thead>
                        <tr>
                           <th>#</th>
                           <th>Player</th>
                           <th>Pos</th>
                           <th>PPG</th>
                        </tr>
                     </thead>

                     <tbody>
                        {roster.slice(0, 6).map((item) => (
                           <tr key={item.id}>
                              <td>{item.jersey_number || "—"}</td>
                              <td>
                                 <div className="sh-bball-team-player-cell">
                                    <div className="sh-bball-team-player-avatar">
                                       {getInitials(item.player)}
                                    </div>
                                    <span>
                                       {item.player?.firstName} {item.player?.lastName}
                                    </span>
                                 </div>
                              </td>
                              <td>{item.position || "—"}</td>
                              <td>—</td>
                           </tr>
                        ))}
                     </tbody>
                  </table>

                  <div className="sh-bball-team-card-footer">
                     <span>Total Players: {roster.length}</span>
                     <button>View Full Roster</button>
                  </div>
               </div>

               <div className="sh-bball-team-card sh-bball-team-results-card">
                  <div className="sh-bball-team-card-header">
                     <h3>Recent Results</h3>
                     <a>
                        View All Results <ChevronRight size={15} />
                     </a>
                  </div>

                  <table className="sh-bball-team-table">
                     <thead>
                        <tr>
                           <th>Date</th>
                           <th>Opponent</th>
                           <th>Result</th>
                           <th>Score</th>
                        </tr>
                     </thead>

                     <tbody>
                        {results.length > 0 ? (
                           results.map((game) => {
                              const result = getResult(game);

                              return (
                                 <tr key={game.id}>
                                    <td>{formatDate(game.date)}</td>
                                    <td>vs {getOpponentName(game)}</td>
                                    <td>
                                       <span
                                          className={
                                             result === "Win"
                                                ? "sh-bball-team-result-win"
                                                : result === "Loss"
                                                   ? "sh-bball-team-result-loss"
                                                   : "sh-bball-team-result-tie"
                                          }
                                       >
                                          {result}
                                       </span>
                                    </td>
                                    <td>{getScore(game)}</td>
                                 </tr>
                              );
                           })
                        ) : (
                           <tr>
                              <td colSpan="4">No completed games yet.</td>
                           </tr>
                        )}
                     </tbody>
                  </table>

                  <div className="sh-bball-team-card-footer sh-bball-team-card-footer--right">
                     <button>View Full Results</button>
                  </div>
               </div>

               <div className="sh-bball-team-card sh-bball-team-leaders-card">
                  <div className="sh-bball-team-card-header">
                     <h3>Team Leaders</h3>
                  </div>

                  <div className="sh-bball-team-leader-list">
                     {leaders.map((leader) => {
                        const Icon = leader.icon;

                        return (
                           <div className="sh-bball-team-leader-row" key={leader.label}>
                              <Icon size={26} />

                              <div className="sh-bball-team-leader-text">
                                 <strong>{leader.label}</strong>
                                 <span>{leader.sub}</span>
                              </div>

                              <div className="sh-bball-team-leader-player">
                                 <div className="sh-bball-team-player-avatar">{leader.initials}</div>
                                 <span>{leader.name}</span>
                              </div>

                              <div className="sh-bball-team-leader-stat">
                                 <strong>{leader.value}</strong>
                                 <span>{leader.stat}</span>
                              </div>
                           </div>
                        );
                     })}
                  </div>
               </div>

               <div className="sh-bball-team-card sh-bball-team-achievements-card">
                  <div className="sh-bball-team-card-header">
                     <h3>Achievements</h3>
                  </div>

                  <div className="sh-bball-team-achievement-list">
                     {achievements.map((item) => {
                        const Icon = item.icon;

                        return (
                           <div className="sh-bball-team-achievement-row" key={item.title}>
                              <Icon size={44} />

                              <div>
                                 <strong>{item.title}</strong>
                                 <span>{item.season}</span>
                                 <p>{item.text}</p>
                              </div>
                           </div>
                        );
                     })}
                  </div>
               </div>

               <div className="sh-bball-team-card sh-bball-team-history-card">
                  <div className="sh-bball-team-card-header">
                     <h3>Season History</h3>
                  </div>

                  <table className="sh-bball-team-table">
                     <thead>
                        <tr>
                           <th>Season</th>
                           <th>Record</th>
                           <th>Win %</th>
                           <th>Finish</th>
                        </tr>
                     </thead>

                     <tbody>
                        {seasonHistory.map((season) => (
                           <tr key={season.season}>
                              <td>{season.season}</td>
                              <td>{season.record}</td>
                              <td>{season.win}</td>
                              <td>{season.finish}</td>
                           </tr>
                        ))}
                     </tbody>
                  </table>

                  <div className="sh-bball-team-card-footer sh-bball-team-card-footer--right">
                     <button>View Full History</button>
                  </div>
               </div>

               <div className="sh-bball-team-card sh-bball-team-about-card">
                  <div className="sh-bball-team-card-header">
                     <h3>About {team?.name}</h3>
                  </div>

                  <p className="sh-bball-team-about-copy">
                     {team?.name} is competing in {team?.league?.name || "this league"} during the{" "}
                     {team?.season?.name || "current season"}.
                  </p>

                  <div className="sh-bball-team-about-details">
                     <div>
                        <UserRound size={18} />
                        <span>Captain</span>
                        <strong>{team?.captain || "—"}</strong>
                     </div>

                     <div>
                        <House size={18} />
                        <span>Season</span>
                        <strong>{team?.season?.name || "—"}</strong>
                     </div>

                     <div>
                        <MapPinIcon />
                        <span>Home Court</span>
                        <strong>{nextGame?.location || "—"}</strong>
                     </div>

                     <div>
                        <Shield size={18} />
                        <span>League</span>
                        <strong>{team?.league?.name || "—"}</strong>
                     </div>
                  </div>
               </div>
            </section>
         </main>
      </div>
   );
}

function MapPinIcon() {
   return (
      <svg
         width="18"
         height="18"
         viewBox="0 0 24 24"
         fill="none"
         stroke="currentColor"
         strokeWidth="2"
         strokeLinecap="round"
         strokeLinejoin="round"
      >
         <path d="M20 10c0 6-8 12-8 12S4 16 4 10a8 8 0 0 1 16 0Z" />
         <circle cx="12" cy="10" r="3" />
      </svg>
   );
}

export default PlayersTeamBasketball;